<template>
  <div class="vyopta-single-filter" :class="{ 'quick-pick': isQuickPick }">
    <div v-if="legacyMode" class="header"></div>
    <div v-if="legacyMode" class="pillOnly">
      <span class="pillText">{{ filter.text }}</span>
      <i class="remove-filter fa fa-times" @click="removeFilter"></i>
    </div>
    <div v-if="!legacyMode" class="newFilter">
      <div class="header">
        <div class="leftHeader" :class="{ 'static': isStatic }">
          <span v-if="currentFilter.field" class="fieldName" :class="{ 'qualityFilterHeader': qualitySpecialCase }">
            {{ currentFilter.fieldText }}
            <i-v-tooltip v-if="showGenericTooltip && !showQualityTooltip" color="var(--tooltip-background)" top z-index="10000" content-class="above single-filter-quick-pick-tt" interactive :opacity="1">
              <template v-slot:activator="{ on }">
                  <span class="info-btn"><i v-on="on" class="far fa-info-circle"></i></span>
              </template>
              <div class="quick-pick-tt quick-pick-tt-legend">
                <div class="quick-pick-tt-legend-item" v-for="(config, index) in quickPickFilterConfig" :key="index">
                  <div v-if="config.icon" :class="config.colorClass" class="quick-pick-tt-legend-icon"><i :class="config.icon"></i></div>
                  <div v-if="config.svgSrc" :class="config.colorClass" class="quick-pick-tt-legend-svg"><img :src="config.svgSrc" :class="config.svgClass"/></div>
                  <div class="quick-pick-tt-legend-label">{{ config.legendTitle }}</div>
                </div>
              </div>
            </i-v-tooltip>
            <i-v-tooltip v-if="showQualityTooltip" color="var(--tooltip-background)" top z-index="10000" content-class="above single-filter-quick-pick-tt" interactive :opacity="1">
              <template v-slot:activator="{ on }">
                  <span class="info-btn"><i v-on="on" class="far fa-info-circle"></i></span>
              </template>
              <div class="quick-pick-tt">
                <div class="quick-pick-tt-header">
                  <i class="fas fa-check-circle semantic-good"></i><span class="quick-pick-tt-header-text">Good</span>
                  <span class="quick-pick-tt-header-middle-text"><i class="fas fa-minus-circle semantic-fair"></i><span class="quick-pick-tt-header-text">Fair</span></span>
                  <i class="fas fa-exclamation-circle semantic-bad"></i><span class="quick-pick-tt-header-text">Bad</span>
                </div>
                <div class="quick-pick-tt-body">WXP Collaboration quality algorithm</div>
                <div class="quick-pick-tt-header"><i class="fas fa-question-circle semantic-neutral"></i><span class="quick-pick-tt-header-text">Unknown</span></div>
                <div class="quick-pick-tt-body">Quality data unavailable</div>
                <div class="quick-pick-tt-link"><a :href="brandingService.getSupportUrl('qualityImprovements')" target='_blank' title='Learn More'>Learn More</a></div>
              </div>
            </i-v-tooltip>
          </span>
          <span v-if="currentFilter.operatorText" class="operatorName" :class="{ 'qualityFilterHeader': qualitySpecialCase, 'truncateOperator': isTruncatedOperator }" @click="operatorHeaderClicked">
            {{  currentFilter.operatorText  }}
          </span>
        </div>
        <div class="rightHeader">
          <i class="delete fas fa-times-circle" @click="removeFilter" title="Delete"></i>
        </div>
      </div>
      <div :class="{ field: true, shown: !fieldComplete }">
        <v-autocomplete
          ref="fieldInput"
          @hook:mounted="fieldInputMounted"
          v-model="currentFilter.field"
          class="autocomplete"
          placeholder="Search Attributes"
          :items="fieldOptions"
          solo
          @input="handleFieldSubmit"
          :menu-props="{contentClass:'field-input-menu'}"
        >
        </v-autocomplete>
      </div>
      <div :class="{ operator: true, shown: fieldComplete && !operatorComplete }">
        <v-select
          ref="selectInput"
          :items="getOperators()"
          solo
          placeholder="Select Operator"
          @input="onOperatorChange"
          :menu-props="{contentClass:'optionsMenu', closeOnContentClick: true, maxHeight: '75%'}"
        >
          <template v-slot:no-data>
            <div style="height:0;width:0;"/>
          </template>
          <template v-slot:prepend-item class="custom-list-items">
            <v-list-item v-if="quickPickImage" @click="handleQuickPickSelected">
              <div class="quick-pick-text">is</div>
              <img class="quick-pick-image" :src="quickPickImage" />
            </v-list-item>
            <v-divider v-if="quickPickImage"></v-divider>
            <v-list-item v-if="hasKnownValuesAndNotAList" @click="() => onOperatorChange({text: 'in', value: '=in=(|)', useId: true})">
              <div class="option-text">in</div>
              <img class="option-image" src="dist/images/in-icon.png" />
            </v-list-item>
            <v-list-item v-if="hasKnownValuesAndNotAList" @click="() => onOperatorChange({text: 'not in', value: '=out=(|)', useId: true})">
              <div class="option-text">not in</div>
              <img class="option-image" src="dist/images/not-in-icon.png" />
            </v-list-item>
            <v-divider v-if="hasKnownValuesAndNotAList"></v-divider>
          </template>
        </v-select>
      </div>
      <div :class="{ value: true, shown: fieldComplete && operatorComplete }">
        <v-text-field
          ref="textInput"
          v-model="currentFilter.queryVar"
          solo
          placeholder="Enter value"
          :class="{textValue: true, shown: valueType ===  'text' && !isQuickPick, complete: filterComplete}"
          @keydown="onChange"
          @blur="onValueSubmit"
          @click="onValueClick"
        ></v-text-field>
        <v-text-field
          v-model="currentFilter.shortText"
          solo
          disabled
          :placeholder="currentFilter.operatorText === 'is empty' ? 'no value':'any value'"
          :class="{textValue: true, shown: valueType ===  'noValue', complete: true}"
          @click="onValueClick"
        ></v-text-field>
        <div
          ref="select"
          :class="{ filterValueSelect: true, shown: valueType ===  'list' }"
          :style="filterWidth?'min-width:'+filterWidth+';':''"
        >
          <div
            v-if="!showList"
            v-dompurify-html="currentFilter.shortText || currentFilter.text || 'All'"
            :class="{listValueDisplay: true, filterLookalike: true, complete: filterComplete}"
            @click="onListValueClick"
          ></div>
          <div :class="{optionsBox: true, shown: showList}">
            <treeselect 
                ref="listSearch"
                v-model="selectedValuesArray"
                :options="newFilterValues"
                :show-count="true"
                show-count-of="LEAF_DESCENDANTS"
                value-consists-of="LEAF_PRIORITY"
                :disable-branch-nodes="true"
                :flatten-search-results="false"

                :close-on-select="false"
                :always-open="true"
                
                :defaultExpandLevel="0"

                search-nested
                :disable-fuzzy-matching="true"
                
                :multiple="true"
                placeholder="Filter options"
                :normalizer="normalizer"
                :limit="selectionLimit"
                value-format="object"
            >
              <label slot="option-label" slot-scope="{ node, shouldShowCount, count, labelClassName, countClassName }" :class="node.isBranch && node.isRootNode ? 'selectOption tree-branch ' + labelClassName : 'selectOption ' + labelClassName ">
                <span :class="node.raw.class ? node.raw.class + ' optionName': 'optionName'"><i v-if="node.raw.fa" :class="node.raw.fa"></i> {{ node.label }}
                  <span v-if="shouldShowCount" :class="countClassName">({{ count }})</span>
                </span>
                <span v-if="node.raw.beta" class="new-beta badge">beta</span>
                <img v-if="node.raw.imgUrl" class="optionImage" :src="node.raw.imgUrl" />
              </label>
            </treeselect>
          </div>
        </div>
        <div v-if="isQuickPick" class="quick-pick">
          <QuickPickFilter
            :widget="filter.widget"
            :datasetName="filter.datasetId || datasetName"
            :fieldName="quickPickFieldName"
            :initialSelection="quickPickSelection"
            @quick-pick-updated="handleQuickPickUpdated"
          >
          </QuickPickFilter>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import BrandingService from '../../../services/branding.service';
import QuickPickFilter from '../../quickPickFilter/quickPickFilter.vue';
import InteractiveVTooltip from '../../../modules/assurance/components/interactive-vtooltip/InteractiveVTooltip.vue';
import QuickPickService from '../../../services/quick-pick.service';
import filterExpressionService from '../../../modules/assurance/services/filter.expression.service';
import UtilsService from "../../../services/utils.service";
import DomPurify from "dompurify";
import Treeselect from '@riophae/vue-treeselect'
import '@riophae/vue-treeselect/dist/vue-treeselect.css'

// Available filter types.
  // A column's filterType property determines which filters are available for the column.
  // The default filter type is the "string" filter, so any columns with no filterType will use that.
  // Unfilterable columns should use the filterType "none" to disable filtering on that column.
  const DEFAULT_FILTER_TYPE = "string";

  const STRING_FILTERS = [
    { text: 'contains', value: { text: 'contains', value: '=="*|*"' } },
    { text: 'does not contain', value: { text: 'does not contain', value: '!="*|*"' } },
    { text: 'equals', value: { text: 'equals', value: '=="|"' } },
    { text: 'does not equal', value: { text: 'does not equal', value: '!="|"' } },
    { text: 'starts with', value: { text: 'starts with', value: '=="|*"' } },
    { text: 'ends with', value: { text: 'ends with', value: '=="*|"' } }
  ];

  const UUID_FILTERS = [
    { text: 'equals', value: { text: 'equals', value: '=="|"' } },
    { text: 'does not equal', value: { text: 'does not equal', value: '!="|"' } }
  ];

  const NUMBER_FILTERS = [
    { text: 'equals', value: { text: 'equals', value: '==' } },
    { text: 'does not equal', value: { text: 'does not equal', value: '!=' } },
    { text: 'less than', value: { text: 'less than', value: '=lt=' } },
    { text: 'less than or equal to', value: { text: 'less than or equal to', value: '=le=' } },
    { text: 'greater than', value: { text: 'greater than', value: '=gt=' } },
    { text: 'greater than or equal to', value: { text: 'greater than or equal to', value: '=ge=' } }
  ];

  const METRIC_TAG_OPERATIONS = [
    { text: 'contains', value: { text: 'contains', value: 'tags=="|=*|*"' } },
    { text: 'does not contain', value: { text: 'does not contain', value: 'tags!="|=*|*"' } },
    { text: 'equals', value: { text: 'equals', value: 'tags=="|=|"' } },
    { text: 'does not equal', value: { text: 'does not equal', value: 'tags!="|=|"' } },
    { text: 'starts with', value: { text: 'starts with', value: 'tags=="|=|*"' } },
    { text: 'ends with', value: { text: 'ends with', value: 'tags=="|=*|"' } },
    { text: 'is empty', value: { text: 'is empty', value: 'tags=="|="' } },
    { text: 'is not empty', value: { text: 'is not empty', value: 'tags!="|="' } }
  ];

  const METRIC_TAG_LIST_OPERATIONS = [
    { text: 'in', value: { text: 'in', value: 'tags=in=(|)', useId: true } },
    { text: 'not in', value: { text: 'not in', value: 'tags=out=(|)', useId: true } }
  ];

  const METRIC_STAR_RATING_OPERATIONS = [
    { text: 'contains', value: { text: 'contains', value: 'tags=="|=|"' } },
    { text: 'does not contain', value: { text: 'does not contain', value: 'tags!="|=|"' } }
  ];

  const STRING_LIST_FILTERS = [
    { text: 'contains', value: { text: 'contains', value: '=="*|*"' } },
    { text: 'does not contain', value: { text: 'does not contain', value: '!="*|*"' } },
  ];

  const NUMBER_LIST_FILTERS = [
    { text: 'contains', value: { text: 'contains', value: '==|' } },
    { text: 'does not contain', value: { text: 'does not contain', value: '!=|' } },
  ];

  const FILTER_MAP = {
    "string": STRING_FILTERS,
    "number": NUMBER_FILTERS,
    "uuid": UUID_FILTERS,
    "list": STRING_LIST_FILTERS,
    "metric": METRIC_TAG_OPERATIONS,
    "metricList": METRIC_TAG_LIST_OPERATIONS,
    "starRating": NUMBER_LIST_FILTERS,
    "metricStarRating": METRIC_STAR_RATING_OPERATIONS,
    "none": []
  };

  const DEFAULT_OPERATOR = {
    "string": 'contains',
    "number": 'equals',
    "uuid": 'equals',
    "list": 'contains',
    "metric": 'contains',
    "metricList": 'in',
    "starRating": 'contains',
    "metricStarRating": 'contains',
    "none": []
  };

  const PERIPHERAL_FIELDS = {
    'camera': true,
    'display': true,
    'microphone': true,
    'touch_panel': true
  };

  const QUICK_PICK_TRUNCATE_MAP = {
    'microphone': true,
    'touch_panel': true,
    'connection_type': true,
    'quality': true
  }

  const QUICK_PICK_FILTER_TYPES = QuickPickService.getFilterTypes();
  const QUICK_PICK_IMAGES = QuickPickService.getImageMap();

function getValuesFromFilter(filter, filterValues) {
  let newSelected = [];

  function getFilter(fValues, value) {
    var results = [];
    fValues.forEach(filterValue => {
      if (filterValue.id === value) {
        results.push(filterValue);
      }
      if (filterValue.children) {
        results.push(...(getFilter(filterValue.children, value)));
      }
    })
    return results;
  }

  if (filter && filter.queryVar) {
    let values = filter.queryVar.split(',');
    values.forEach(value => {
      newSelected.push(...(getFilter(filterValues, value)));
    });
  }
  return newSelected;
}

export default {
    name: 'SingleFilter',
    components: {
      QuickPickFilter,
      'i-v-tooltip': InteractiveVTooltip,
      Treeselect
    },
    props: {
      columns: {
        type: Array,
        default: () => []
      },
      datasetName: {
        type: String,
        default: ''
      },
      excludedQuickPicks: {
        type: Object,
        default: () => {}
      },
      filter: {
        type: Object,
        default: () => {}
      },
      filterId: {
        type: Number,
        default: -1
      },
      isStatic: {
        type: Boolean,
        default: false
      },
      placeholder: {
        type: String,
        default: 'Add Filter: e.g. Target contains John'
      }
    },
    data: function () {
      if (this.filter && this.filter.operatorText === 'quick pick') {
          this.filter.operatorText = 'is';
      }
      const isDarkMode = document.body.classList.contains('dark');
      return {
        brandingService: BrandingService,
        currentFilter: Object.assign(Object.create(Object.getPrototypeOf(this.filter)), this.filter),
        autocompleteKey: 1,
        fieldComplete: this.filter && !!this.filter.field,
        filterComplete: this.filter && this.filter.filterComplete,
        fieldOptions: [],
        filterValues: [],
        isSelecting: false,
        isQuickPick: this.filter && this.filter.isQuickPick,
        quickPickSelection: this.filter && this.filter.quickPickValues ? this.filter.quickPickValues : undefined,
        legacyMode: false,
        showList: false,
        operatorComplete: this.filter && !!this.filter.operatorText,
        selectedValuesArray: [],
        searchText: '',
        valueType: this.filter ? this.getValueType(this.filter.type, this.filter.operatorText) : 'text',
        selectionLimit: 3,
        observer: null,
        darkMode: isDarkMode,
      };
    },
    computed: {
      hasKnownValues: function() {
        return this.currentFilter.knownValuePath || this.currentFilter.knownValues;
      },
      hasKnownValuesAndNotAList: function() {
        return this.currentFilter.type !== 'list' && this.currentFilter.type !== 'metricList' && this.hasKnownValues;
      },
      filterWidth: function() {
        let column = this.columns.filter((column) => (column.field === this.currentFilter.field || column.displayField === this.currentFilter.field) && column.filterType === this.currentFilter.type);
        if (column && column[0]) {
          return column[0].filterWidth || '198px';
        }
        return '198px';
      },
      quickPickFieldName: function() {
        return this.currentFilter.quickPickId ? this.currentFilter.quickPickId : this.currentFilter.field;
      },
      quickPickFilterConfig: function() {
        // We only need this for the tooltip legends, and we want to exclude "Other" from those
        return _.get(QUICK_PICK_FILTER_TYPES, [this.datasetName, this.quickPickFieldName]).filter(config => !(config.title === 'Other' && config.value === 'Other'));
      },
      quickPickImage: function() {
        let isQuickPickExcluded = !!_.get(this.excludedQuickPicks, [this.datasetName, this.currentFilter.field]);
        let newVar = isQuickPickExcluded ? false :
            _.get(QUICK_PICK_IMAGES, [this.datasetName, this.quickPickFieldName]);
        if (newVar) {
          newVar = newVar.replace('mode', this.darkMode?'dark':'light');
        }
        return newVar;
      },
      showGenericTooltip: function() {
        return (this.isQuickPick || this.currentFilter.operatorText === 'quick pick'|| this.currentFilter.operatorText === 'is') && !!_.get(QUICK_PICK_FILTER_TYPES, [this.datasetName, this.quickPickFieldName]);
      },
      showQualityTooltip: function() {
        return this.currentFilter && this.currentFilter.field ? this.currentFilter.field === 'quality' || this.currentFilter.field === 'avg_quality' : false;
      },
      qualitySpecialCase: function() {
        return this.currentFilter && this.currentFilter.field && this.currentFilter.fieldText ?
          this.currentFilter.field === 'quality' && (this.currentFilter.fieldText === 'Quality (Latest)' || this.currentFilter.fieldText === 'Quality (Overall)') : false;
      },
      isTruncatedOperator: function() {
        if (this.isQuickPick && this.currentFilter && this.currentFilter.field && QUICK_PICK_TRUNCATE_MAP[this.currentFilter.field]) {
          if (this.currentFilter.field === 'quality' && this.currentFilter.type && this.currentFilter.type === 'metric') {
            // special case because metrics filters don't use the clunky "Quality (Latest)" attribute name and thus don't need to be truncated
            return false;
          }
          return true;
        }
        return false;
      },
      newFilterValues: function() {
        let filterValuesCopy = JSON.parse(JSON.stringify(this.filterValues));
        return filterValuesCopy
            .filter((value) => value.name.toLowerCase().indexOf(this.searchText.toLowerCase()) > -1)
            .map((value) => {
              // todo add back in
              // if (this.searchText) {
              //   let searchTextIndex = value.name.toLowerCase().indexOf(this.searchText.toLowerCase());
              //   let textLength = this.searchText.length;
              //   value.name = value.name.substr(0, searchTextIndex)
              //       + '<strong>'
              //       + value.name.substr(searchTextIndex, textLength) +
              //       '</strong>'
              //       + value.name.substr(searchTextIndex+textLength);
              // }
              return value;
            });
      },
      openFilterValues: function() {
        const c = this.columns.filter((column) => (column.field === this.currentFilter.field || column.displayField === this.currentFilter.field) && column.filterType === this.currentFilter.type)[0];
        
        return c && c.knownValues ? c.knownValues.filter(v => v.open).map(v => v.id) : undefined;
      },
    },
    watch: {
      columns: {
        deep: true,
        immediate: true,
        handler: function(columns) {
          this.columns = columns;
          this.setupFilterTypeahead();
        }
      },
      filter: {
        deep: true,
        immediate: true,
        handler: function(filter) {
          if (!filter) {return;}
          if (filter.operatorText === 'quick pick') {
            filter.operatorText = 'is';
          }
          // this.currentFilter = Object.assign(Object.create(Object.getPrototypeOf(filter)), filter);
          this.currentFilter = JSON.parse(JSON.stringify(filter));

          // preempivily update this.filterValues and this.currentFilter.knownValues based off this.currentFilter.knownValuePath if needed
          let promise;
          if (!this.currentFilter.knownValues && this.currentFilter.knownValuePath) {
            promise = filterExpressionService.getKnownValues(this.currentFilter.knownValuePath).then((values) => {
              if(this.currentFilter.valuesSubset && this.currentFilter.valuesSubset.length) {
                values = values.filter(val => this.currentFilter.valuesSubset.includes(val.id));
              }
              this.currentFilter.knownValues = [...values].sort(compareName);
            });
          } else {
            promise = Promise.resolve();
          }

          promise.then(() => {
            if (filter.text && !filter.fieldText) {
              this.legacyMode = true;
            } else {
              // if (this.currentFilter.knownValues) {
                // this.filterValues = this.generateFilterValues(this.currentFilter.knownValues); // this is done in onOperatorChange
                // this.getValuesFromFilter(this.currentFilter); // this being done twice
              // }

              this.legacyMode = false;
              this.fieldComplete = filter && !!filter.field;
              this.onOperatorChange(this.findOperator(filter.operatorText, filter.operatorValue), true);
              this.operatorComplete = filter && !!filter.operatorText;
              this.filterComplete = filter && filter.filterComplete;
              this.isQuickPick = filter && filter.isQuickPick;
              this.quickPickSelection = filter && filter.quickPickValues ? filter.quickPickValues : undefined;
              this.getValuesFromFilter(filter);
              this.valueType = this.getValueType(filter.type, filter.operatorText);
              this.isSelecting = this.isListType(filter.type);
              this.$forceUpdate();
            }
          });
        }
      },
      filterId: function(filterId){
        this.filterId = filterId;
      },
      showList: function(showList){
        if (!showList) {
          this.resetSelectionLimit();
        }
      }
    },
    mounted () {
      let self = this;
      self.observer = new MutationObserver(function(mutationsList, observer) {
        for (const mutation of mutationsList) {
          if (mutation.type === "childList") {
            const limitEl = self.$el.querySelector('.vue-treeselect__limit-tip-text');
            if (limitEl && mutation.addedNodes && mutation.addedNodes.length > 0 &&  mutation.addedNodes[0] === limitEl.parentElement) {
              limitEl.style.cursor = 'pointer'; // todo reset somewhere
              limitEl.addEventListener('click', self.removeSelectionLimit.bind(self), true);
            } else if (mutation.addedNodes && mutation.addedNodes.length > 0)  {
              const showLessEl = self.$el.querySelector('#show-less');
              if (showLessEl && mutation.addedNodes[0] !== showLessEl) {
                const placeHolderEl = self.$el.querySelector('.vue-treeselect__placeholder');
                const parentNode = placeHolderEl.parentNode;
                showLessEl.remove();
                self.$nextTick(() =>  {
                  parentNode.insertBefore(showLessEl, placeHolderEl);
                });
              }
            }
            
            if (mutation.removedNodes && mutation.removedNodes.length > 0 && !self.$el.querySelector('.vue-treeselect__multi-value-item-container')) {
              const showLessEl = self.$el.querySelector('#show-less');
              if (showLessEl && mutation.removedNodes[0] !== showLessEl) {
                self.resetSelectionLimit();
              }
            }
          }
        }
      });
      const target = self.$el.querySelector('.vue-treeselect__multi-value');
      if (target !== null) {
        self.observer.observe(target, {
          childList: true
        });
      }

      self.lastClassString = document.body.classList.toString();

      self.observer2 = new MutationObserver((mutationList) => {
        for (const item of mutationList) {
          if (item.attributeName === "class") {
            const classString = document.body.classList.toString();
            if (classString !== self.lastClassString) {
              self.darkMode = document.body.classList.contains('dark');
              break;
            }
          }
        }
      });

      self.observer2.observe(document.body, { attributes: true });

    },
    beforeDestroy: function() {
      document.removeEventListener('click', this.outsideClickListener);
      document.removeEventListener('click', this.outsideOperatorClickListener);
      if (this.observer) { this.observer.disconnect(); }
      if (this.observer2) { this.observer2.disconnect(); }
    },
    methods: {
      removeSelectionLimit: function(event) {
        let self = this;
        if (event) {
          event.stopPropagation();
        }
        const placeHolderEl = self.$el.querySelector('.vue-treeselect__placeholder');
        self.selectionLimit = undefined;
        //<div class="vue-treeselect__limit-tip vue-treeselect-helper-zoom-effect-off"><span class="vue-treeselect__limit-tip-text" style="cursor: pointer;">and 1 more</span></div>
        const showLessEl = new DOMParser().parseFromString('<div id="show-less" class="vue-treeselect__limit-tip vue-treeselect-helper-zoom-effect-off vue-treeselect__multi-value-item--transition-enter-to"><span class="vue-treeselect__limit-tip-text" style="cursor: pointer;">Show less</span></div>', "text/html").querySelector('div');
        showLessEl.addEventListener('click', self.resetSelectionLimit.bind(self), true);
        const parentNode = placeHolderEl.parentNode;
        self.$nextTick(() =>  {
          parentNode.insertBefore(showLessEl, placeHolderEl);
        });

      },
      resetSelectionLimit: function (event) {
        let self = this;
        if (event) {
          event.stopPropagation();
        }
        const showLessEl = self.$el.querySelector('#show-less');
        if (showLessEl) {
          showLessEl.remove();
          self.selectionLimit = 3;
        }
      },
      normalizer: function (node) {
        return {
          id: node.id,
          label: node.name,
          children: node.children,
          imgUrl: node.imgUrl,
        }
      },
      filterName: function(value) {
        if (this.searchText) {
          let searchTextIndex = value.toLowerCase().indexOf(this.searchText.toLowerCase());
          if (searchTextIndex !== -1) {
            let textLength = this.searchText.length;
            return DomPurify.sanitize(value.substr(0, searchTextIndex))
                + '<strong>'
                + DomPurify.sanitize(value.substr(searchTextIndex, textLength)) +
                '</strong>'
                + DomPurify.sanitize(value.substr(searchTextIndex + textLength));
          }
        } 
        return DomPurify.sanitize(value);
      },
      fieldInputMounted: function() {
        // fix bug in vuetify if you change the max height of an autocomplete/v-seselct it doesn't buffer enough items 
        if (this.$refs.fieldInput) {
          this.$refs.fieldInput.lastItem = Math.max(this.$refs.fieldInput.lastItem, 200);
        }
      },
      searchInput: function(event) {
        if ((event.key === 'Tab' || event.key === 'Enter') && self.selectedValuesArray.length > 0) {
          this.doneSelecting();
        }
        return true;
      },
      doneSelecting: function() {
        document.removeEventListener('click', this.outsideClickListener);
        let self = this;
        this.showList = false;
        if (self.selectedValuesArray.length > 0) {
          self.currentFilter.text = '';
          self.currentFilter.shortText = '';
          self.currentFilter.value = '';
          self.selectedValuesArray.forEach(function(value) {
            let name = value.name;
            let valueId = value.id;
            let connectingString = ', ';
            let shortConnectingString = ', ';
            let connectingOperator = ',';
            if (self.currentFilter.operatorText === 'does not contain' || self.currentFilter.operatorText === 'not in') {
              connectingOperator = ';'
            }
            // prepend
            if (self.currentFilter.type === 'list' ||
                self.currentFilter.operatorText === 'in' || self.currentFilter.operatorText === 'not in' ||
                self.currentFilter.type === 'metricStarRating' ||
                self.currentFilter.type === 'starRating'
            ) {
              if (self.currentFilter.text) {
                self.currentFilter.text += connectingString;
              } else {
                self.currentFilter.text += self.currentFilter.fieldText + ' ' + self.currentFilter.operatorText + ' (';
              }
              if (self.currentFilter.shortText) {
                self.currentFilter.shortText += shortConnectingString;
              }
            }
            // main work
            self.currentFilter.text += escapeSpecialChars(name);
            self.currentFilter.shortText += escapeSpecialChars(name);
            self.currentFilter.value += self.currentFilter.field + self.currentFilter.operatorValue.replace('|', escapeSpecialChars(valueId)) + connectingOperator;
          });
          if (self.currentFilter.type === 'list' ||
              self.currentFilter.operatorText === 'in' || self.currentFilter.operatorText === 'not in' ||
              self.currentFilter.type === 'metricStarRating' ||
              self.currentFilter.type === 'starRating'
          ) {
            if (self.currentFilter.text) self.currentFilter.text += ')'
          }

          const selectedKeys = self.selectedValuesArray.map(value => value.id);
          self.currentFilter.queryVar = selectedKeys.map(key => escapeSpecialChars(key)).join();
          self.currentFilter.value = self.currentFilter.value.substring(0, self.currentFilter.value.length - 1);
          if (self.currentFilter.operatorText === 'in' || self.currentFilter.operatorText === 'not in') {
            if (self.currentFilter.type === 'string') {
              let keys = selectedKeys.map(key => '"' + key + '"').join(',');
              self.currentFilter.value = self.currentFilter.field + self.currentFilter.operatorValue.replace('|', keys);
            } else if (self.currentFilter.type === 'metricList') {
              let field = this.currentFilter.field;
              let keys = selectedKeys.map(key => '"' + field + '=' + key + '"').join(',');
              self.currentFilter.value = self.currentFilter.operatorValue.replace('|', keys);
            } else if (self.currentFilter.type === 'metric') {
              let field = this.currentFilter.field;
              let keys = selectedKeys.map(key => '"' + field + '=' + key + '"').join(',');
              self.currentFilter.value = 'tags' + self.currentFilter.operatorValue.replace('|', keys);
            }
          }
          this.filterComplete = true;
        } else {
          self.currentFilter.queryVar = '';
          self.currentFilter.text = '';
          self.currentFilter.shortText = '';
          self.currentFilter.value = '';
          self.currentFilter.filterComplete = true;
          this.filterComplete = true;
          // this.$emit('filter-emptied');
          // return;
        }
        self.currentFilter.id = this.filterId;
        self.currentFilter.filterComplete = self.filterComplete;
        this.$emit('filter-selected', self.currentFilter);
      },
      isListType: function(type) {
        return type === 'list' || type === 'metricList' || type === 'starRating' || type === 'metricStarRating';
      },
      getValueType: function(type, operatorText) {
        let isListType = this.isListType(type);
        if ((isListType && (operatorText === 'contains' || operatorText === 'does not contain')) || (operatorText === 'in' || operatorText === 'not in')) {
          return 'list';
        }
        if (operatorText === 'quick pick' || operatorText === 'is') {
          return 'quickPick';
        }
        if (operatorText === 'is not empty' || operatorText  === 'is empty') {
          return 'noValue';
        }
        return  'text';
      },
      onChange: function(event) {
        if (event.key === 'Tab' || event.key === 'Enter') {
          this.onValueSubmit(event);
        } else if (this.currentFilter.type === 'number') {
          if (event.key === '>') {
            event.preventDefault();
            this.onOperatorChange({text: 'greater than', value: {text: 'greater than', value: '=gt='}});
            this.$forceUpdate(); // had to do this to get the operator change to take effect in the UI
            return true;
          } else if (event.key === '<') {
            event.preventDefault();
            this.onOperatorChange({text: 'less than', value: {text: 'less than', value: '=lt='}});
            this.$forceUpdate();
            return true;
          } else if (event.key === '!') {
            event.preventDefault();
            if (this.currentFilter.operatorText === 'does not equal') {
              this.onOperatorChange({text: 'equals', value: {text: 'equals', value: '=='}});
            } else if (this.currentFilter.operatorText === 'greater than') {
              this.onOperatorChange({
                text: 'less than or equal to',
                value: {text: 'less than or equal to', value: '=le='}
              });
            } else if (this.currentFilter.operatorText === 'less than') {
              this.onOperatorChange({
                text: 'greater than or equal to',
                value: {text: 'greater than or equal to', value: '=ge='}
              });
            } else if (this.currentFilter.operatorText === 'greater than or equal to') {
              this.onOperatorChange({text: 'less than', value: {text: 'less than', value: '=lt='}});
            } else if (this.currentFilter.operatorText === 'less than or equal to') {
              this.onOperatorChange({text: 'greater than', value: {text: 'greater than', value: '=gt='}});
            } else {
              this.onOperatorChange({text: 'does not equal', value: {text: 'does not equal', value: '!='}});
            }
            this.$forceUpdate();
            return true;
          } else if (event.key === '=') {
            if (this.currentFilter.operatorText === 'greater than') {
              event.preventDefault();
              this.onOperatorChange({
                text: 'greater than or equal to',
                value: {text: 'greater than or equal to', value: '=ge='}
              });
              this.$forceUpdate();
              return true;

            } else if (this.currentFilter.operatorText === 'less than') {
              event.preventDefault();
              this.onOperatorChange({
                text: 'less than or equal to',
                value: {text: 'less than or equal to', value: '=le='}
              });
              this.$forceUpdate();
              return true;

            } else {
              event.preventDefault();
              this.onOperatorChange({text: 'equals', value: {text: 'equals', value: '=='}});
              this.$forceUpdate();
              return true;

            }
          }
        }
        if (event.key === 'Alt') {
          let operatorsList = this.getOperators();

          if (this.quickPickImage) { // todo use || datasetId
            operatorsList = [{text: 'is', value: 'quickPick'}, ...operatorsList];
          }
          if (this.hasKnownValuesAndNotAList) {
            operatorsList = [{text: 'in', value: '=in=(|)', useId: true}, {text: 'not in', value: '=out=(|)', useId: true}, ...operatorsList];
          }
          
          if (operatorsList.length > 1) {
            for (let i = 0; i < operatorsList.length; i++) {
              const operatorsListElement = operatorsList[i];
              if (operatorsListElement.text === this.currentFilter.operatorText) {
                event.preventDefault();
                this.onOperatorChange(i + 1 < operatorsList.length ? operatorsList[i + 1] : operatorsList[0]);
                this.$forceUpdate();
                return true;
              }
            }
          }
        }
        return true;
      },
      onValueSubmit: function(event) {
        this.filterComplete = true;
        this.currentFilter.id = this.filterId;
        if (this.currentFilter.field && this.currentFilter.type && this.currentFilter.operatorText && this.currentFilter.queryVar) {
          // fill out filter then send
          populateFilterValueAndText(this.currentFilter);
          this.currentFilter.filterComplete = true;
          this.$emit('filter-selected', this.currentFilter);
        } else { //if (filter.isQuickStart) {
          this.currentFilter.value = '';
          this.currentFilter.text = '';
          this.currentFilter.shortText = '';
          this.currentFilter.filterComplete = true;
          this.$emit('filter-selected', this.currentFilter);
        }
        event.target.blur();
      },
      onValueClick: function() {
        this.currentFilter.filterComplete = false;
        this.filterComplete = false;
        this.$emit('filter-on-click', true);
      },
      onListValueClick: function() {
        this.currentFilter.filterComplete = false;
        this.filterComplete = false;
        this.showList = true;
        // add listener to close dialog
        setTimeout(() => document.addEventListener('click', this.outsideClickListener), 0);
        this.$nextTick(() => {
          const input = this.$refs.listSearch.$el.querySelector("input:first-of-type");
          if (input) {
            input.focus();
          }
        });
      },
      onOperatorChange: function(operator, skipMutation) {
        document.removeEventListener('click', this.outsideOperatorClickListener);
        if (!skipMutation) {
          this.currentFilter.filterComplete = false;
          // this.filterComplete = false; // todo
        }
        let wasIn = this.currentFilter.operatorText === 'in' || this.currentFilter.operatorText === "not in";

        function expandValues(kV) {
          return kV.map(value => {
            if (typeof value === 'string') {
              return {id: value, name: value};
            } else if (value.id !== undefined && value.name === undefined) {
              value.name = value.id;
              return value;
            } else if (value.name !== undefined && value.id === undefined) {
              value.id = value.name;
              return value;
            }
            if (value.children) {
              value.children = expandValues(value.children);
            }
            return value;
          });
        }

        if (operator.text && operator.value) {
          this.currentFilter.operatorText = operator.text;
          this.currentFilter.operatorValue = operator.value;

          if (this.columns && this.columns.length > 0) {
            let c = this.columns.filter((column) => column.field === this.currentFilter.field || column.displayField === this.currentFilter.field)[0];
            if (c && c.displayField) {
              this.currentFilter.field = operator.useId ? c.field : c.displayField;
            }
          }
          
          if (wasIn && !(operator.text === 'in' || operator.text === "not in")) {
            // if chaging from in to a different operator, clear the filter
            delete this.currentFilter.queryVar;
            delete this.currentFilter.text;
            delete this.currentFilter.shortText;
            delete this.currentFilter.value;
          }

          if (this.currentFilter.operatorValue !== 'quickPick') {
            if (this.currentFilter.isQuickPick) {
              // if chaging from quick pick to a different operator, clear the filter
              delete this.currentFilter.queryVar;
              delete this.currentFilter.text;
              delete this.currentFilter.shortText;
              delete this.currentFilter.value;
            }
            this.currentFilter.isQuickPick = false;
            this.currentFilter.widget = undefined;
            this.currentFilter.data = undefined;
            this.currentFilter.quickPickValues = undefined;
          }
          this.operatorComplete = true;

          if (operator.text === 'is not empty' || operator.text === 'is empty') {
            this.currentFilter.queryVar = '';
            this.currentFilter.filterComplete = true;
            // this.filterComplete = true; // todo
            this.valueType = 'noValue';
            populateFilterValueAndText(this.currentFilter);
            if (!skipMutation) {
              this.$emit('filter-selected', this.currentFilter);
            }
          }

          this.valueType = this.getValueType(this.currentFilter.type, this.currentFilter.operatorText);
          if (this.isListType(this.valueType)) {
            let column = this.columns.filter((column) => (column.field === this.currentFilter.field || column.displayField === this.currentFilter.field) && (column.filterType === this.currentFilter.type || (this.currentFilter.type === 'list' && column.filterType === 'string')))[0];
            if (column) {
              if (this.currentFilter.knownValues) {
                const values = expandValues(this.currentFilter.knownValues);
                this.filterValues = this.generateFilterValues(values);
                this.getValuesFromFilter(this.currentFilter);
              } else {
                this.filterValues = [];
              }
            }
            if (!skipMutation) {
              this.showList = true;
              // add listener to close dialog
              setTimeout(() => document.addEventListener('click', this.outsideClickListener), 0);

              this.$nextTick(() => {
                const input = this.$refs.listSearch.$el.querySelector("input:first-of-type");
                if (input) {
                  input.focus();
                }
              });
            }
          } else { // for now must be text but if there are other types need to account for them
            if (!skipMutation) {
              this.$nextTick(() => {
                this.$refs.textInput.focus();
              });
            }
          }
        }
        return true;
      },
      outsideClickListener: function(event) {
        if (this.$refs.select && !this.$refs.select.contains(event.target)) {
          if (this.showList) {
            this.doneSelecting();
          }
          this.searchText = '';
          document.removeEventListener('click', this.outsideClickListener);
        }
      },
      outsideOperatorClickListener: function(event) {
        if (this.$refs.selectInput
            && !this.$refs.selectInput.$el.contains(event.target)    
            && this.$refs.selectInput.$refs.menu 
            && this.$refs.selectInput.$refs.menu.$refs.content 
            && !this.$refs.selectInput.$refs.menu.$refs.content.contains(event.target)) {
          if (!this.operatorComplete) {
            this.operatorComplete = true;
            this.filterComplete = true;

            if (this.$refs.selectInput) {
              this.$refs.selectInput.isMenuActive = false;
            }

            this.filterType = 'text';
            if (this.currentFilter) {
              this.isSelecting = this.isListType(this.currentFilter.type);
              this.isQuickPick = this.currentFilter.isQuickPick;
            }
          }
          document.removeEventListener('click', this.outsideOperatorClickListener);
        }
      },
      handleQuickPickSelected: function() {
        document.removeEventListener('click', this.outsideOperatorClickListener);
        this.isQuickPick = true;
        this.operatorComplete = true;
        this.isSelecting = false;
        let filter = this.currentFilter;
        filter.operatorText = 'is';
        filter.operatorValue = 'quickPick';
        filter.isQuickPick = true;
        // filter.value = '';
        // filter.queryVar = '';
        // filter.text = '';
        // filter.shortText = '';

      },
      handleQuickPickUpdated: function(qpType, data, excludedValues, valueDescriptions, excludedDescriptions, onlyClicked, allSelected, otherDeselected) {
        // TODO: If this needs to be a filter expression instead of a single filter,
        // perhaps the business logic to create the filter should be moved to filterExpression.vue

        // todo this is all wrong - need to build filter in the quickpick
        
        let filter = this.currentFilter;
        
        let values = data.selections;

        filter.widget = qpType;
        filter.data = data;
        filter.quickPickValues = values;
        if (qpType === 'toggles') {

          if (allSelected) {
            // if all QP options are selected, don't apply any filter
            filter.value = '';
            filter.queryVar = '';
            filter.text = '';
            filter.shortText = '';
          } else {
            if (PERIPHERAL_FIELDS[filter.field] && filter.type !== 'metric') {
              // special case for peripherals
              // TODO: Peripherals quick pick only works for datasets. Will metrics filters ever need peripherals quick picks?
              contructPeripheralQuickPickFilter(filter);
            } else if (filter.type === 'metric') {
              constructMetricQuickPickFilter(filter, valueDescriptions, excludedValues, excludedDescriptions, onlyClicked, otherDeselected);
            } else {
              constructDatasetQuickPickFilter(filter, valueDescriptions, excludedValues, excludedDescriptions, onlyClicked, otherDeselected);
            }
          }
        } else if (qpType === 'ids') {  
          if (filter.type === 'metric') {
            if (values && values.length > 0) {
              let ids = values.map(s => `"${filter.field}=${s.id}"`).join()
              let names = values.map(s => s.name).join(', ')
              filter.value = `tags=in=(${ids})`;
              filter.queryVar = ids;
              filter.text = `${filter.fieldText} is (${names})`;
              filter.shortText = `${names}`;
            } else {
              // if no QP ids are selected, don't apply any filter
              filter.value = '';
              filter.queryVar = '';
              filter.text = '';
              filter.shortText = '';
            }
          } else {
            if (values && values.length > 0) {
              let ids = values.map(s => s.id).join()
              let names = values.map(s => s.name).join(', ')
              filter.value = `${filter.field}=in=(${ids})`;
              filter.queryVar = ids;
              filter.text = `${filter.fieldText} is (${names})`;
              filter.shortText = names;
            } else {
              // if no QP ids are selected, don't apply any filter
              filter.value = '';
              filter.queryVar = '';
              filter.text = '';
              filter.shortText = '';
            }
          }
        }
        delete filter.auto;
        filter.filterComplete = true;
        this.filterComplete = true;
        this.$emit('filter-selected', filter);
      },
      setupFilterTypeahead: function() {
        let alphabetizedColumns = [...this.columns].sort(compareTitle);
        if (this.fieldOptions) {
          this.fieldOptions.length = 0;
        }
        alphabetizedColumns.forEach(col => {
          if (col.filterType !== 'none') {
            this.fieldOptions.push({
              text: col.title,
              value: col.field
            });
          }
        });
      },
      getOperators: function() {
        if (!this.currentFilter) {
          return [];
        }
        return FILTER_MAP[this.currentFilter.type] || [];
      },
      getResultValue: function(result) {
        return result.text;
      },
      getValuesFromFilter: function(filter) {
        let self = this;
        self.selectedValuesArray = getValuesFromFilter(filter, self.filterValues);
      },
      findOperator: function (operatorText, operatorValue) {
        let matchingOperator = this.getOperators().filter((operator) => operator.text === operatorText);
        if (matchingOperator.length === 0 && operatorText === 'in') {
          return {text: 'in', value: '=in=(|)', useId: true};
        } else if (matchingOperator.length === 0 && operatorText === 'not in') {
          return {text: 'not in', value: '=out=(|)', useId: true};
        }
        if (matchingOperator && matchingOperator.length > 0) {
          return matchingOperator[0].value;
        }
        if (operatorValue) {
          return { text: operatorText, value: operatorValue };
        }
        return { text: undefined, value: undefined };
      }, 
      handleFieldSubmit: function(field) {
        let self = this;
        let column = this.columns.filter((current) => (current.field === field || current.displayField === field))[0];
        let columnType = column.filterType || DEFAULT_FILTER_TYPE;

        this.currentFilter.fieldText = column.title;
        this.currentFilter.field = field;
        this.currentFilter.type = columnType;
        this.currentFilter.filterComplete = false;
        this.fieldComplete = true;
        this.filterComplete = false;
        this.currentFilter.filterComplete = false;
        this.currentFilter.knownValuePath = column.knownValuePath;
        this.currentFilter.knownValues = column.knownValues;
        this.currentFilter.quickPickId = column.quickPickId;

        // premptivly load known values from api and set on current filter before selecting operation
        let promise;
        if (!this.currentFilter.knownValues && this.currentFilter.knownValuePath) {
          promise = filterExpressionService.getKnownValues(this.currentFilter.knownValuePath).then((values) => {
            if(this.currentFilter.valuesSubset && this.currentFilter.valuesSubset.length) {
              values = values.filter(val => this.currentFilter.valuesSubset.includes(val.id));
            }
            this.currentFilter.knownValues = [...values].sort(compareName);
          });
        } else {
          promise = Promise.resolve();
        }

        promise.then(() => {
          // check if operator is still valid if selected
          let operatorText = self.currentFilter.operatorText;
          if (operatorText) {
            let matchingOperator = self.findOperator(operatorText);
            if (matchingOperator.text) { // operator with same name might have different value for different field type
              if (this.valueType === 'list' ) { // todo or filter.operatorText === 'in' || filter.operatorText === 'notin'
                this.selectedValuesArray = []; // values might not apply to next field so reset
                delete this.currentFilter.queryVar;
                delete this.currentFilter.text;
                delete this.currentFilter.shortText;
                delete this.currentFilter.value;
              }
              this.onOperatorChange(matchingOperator);
            } else {
              delete this.currentFilter.operatorText;
              delete this.currentFilter.operatorValue;
              delete this.currentFilter.queryVar;
              delete this.currentFilter.text;
              delete this.currentFilter.shortText;
              delete this.currentFilter.value;
              delete this.currentFilter.isQuickPick;
              delete this.currentFilter.widget;
              delete this.currentFilter.data;
              delete this.currentFilter.quickPickValues;
              this.currentFilter.filterComplete = false;
              this.operatorHeaderClicked();
            }
          } else {
            if (this.quickPickImage) { // todo use || datasetId
              this.handleQuickPickSelected();
              this.currentFilter.filterComplete = true;
              this.filterComplete = true;
              this.$emit('filter-selected', this.currentFilter); // toggle cause this.$emit('quick-pick-updated' but ids do not, so do this too??
            } else if (this.hasKnownValuesAndNotAList) { 
              this.onOperatorChange({text: 'in', value: '=in=(|)', useId: true});
            } else {
              let operatorText = DEFAULT_OPERATOR[this.currentFilter.type];
              let matchingOperator = this.findOperator(operatorText);
              if (matchingOperator.text) {
                this.onOperatorChange(matchingOperator); // { text: operatorText, value: matchingOperator[0].value.value }
              } else {
                this.operatorHeaderClicked();
              }
            }
          }
        });
      },
      fieldHeaderClicked: function() {
        this.fieldComplete = false;
        this.filterComplete = false;
        this.filterType = 'text';
        this.isQuickPick = false;
        this.quickPickSelection = undefined;
        this.isSelecting = false;
        if (this.$refs.fieldInput) {
          this.$nextTick(() => {
            this.$refs.fieldInput.focus();
            this.$refs.fieldInput.activateMenu();
          });
        }
      },
      operatorHeaderClicked: function() {
        if (!this.fieldComplete) {
          return;
        }
        if (this.$refs.selectInput) {
          this.$nextTick(() => {
            this.operatorComplete = false;
            this.filterComplete = false;
            this.filterType = 'text';
            this.isSelecting = false;
            this.isQuickPick = false;

            this.$nextTick(() => {
              this.$refs.selectInput.activateMenu();
              this.$refs.selectInput.focus();
              setTimeout(() => {
                this.operatorComplete = false;
                this.filterComplete = false;
                document.addEventListener('click', this.outsideOperatorClickListener);
              }, 1);
            });
          });
        }
      },
      removeFilter: function(filter) {
        /**
         * Filter removed. First parameter is the removed filter.
         *
         * @type {Object}
         * @type {Array}
         */
        if (this.$refs.selectInput) {
          this.$refs.selectInput.isMenuActive = false;
        }

        if (!this.isStatic) {
          this.currentFilter = {};
          this.fieldComplete = false;
          this.operatorComplete = false;
          this.filterComplete = false;
          this.isQuickPick = false;
          this.quickPickSelection = undefined;
          this.$emit('filter-remove-clicked', filter);
        } else {
          // For static filters, only clear values, not the whole filter
          this.filterComplete = true;
          delete this.currentFilter.queryVar;
          delete this.currentFilter.text;
          delete this.currentFilter.shortText;
          delete this.currentFilter.value;
          this.$emit('static-filter-cleared');
        }
      },
      generateFilterValues(values) {
        function updateFilterForEventLevel(filter) {
          let quality = filter.name,
              cls = 'semantic-text iconpanel',
              iconCls = '';

          if (typeof quality === 'string') {
            switch (quality.toUpperCase()) {
              case 'CRITICAL':
                cls += ' semantic-critical';
                iconCls = 'fas fa-triangle-exclamation';
                break;
              case 'ERROR':
                cls += ' semantic-bad';
                iconCls = 'fas fa-circle-xmark';
                break;
              case 'WARNING':
                cls += ' semantic-fair';
                iconCls = 'fas fa-square-bolt';
                break
              case 'INFO':
                cls += ' semantic-incall';
                iconCls = 'fas fa-circle-info';
                break;
              case 'DEBUG':
                cls += ' semantic-custom';
                iconCls = 'fas fa-bug';
                break;
              case 'NONE':
                cls += ' semantic-none';
                iconCls = 'fas fa-circle-question';
                break;
              default:
                cls += ' semantic-unknown';
            }
          }

          if (iconCls) filter.fa = iconCls;
          filter.class = cls;
        }

        function updateFilterForAdrStatuses(filter) {
          let status = filter.name,
              cls = 'semantic-text iconpanel',
              iconCls = '';

          if (typeof status === 'string') {
            switch (status.toLowerCase()) {
              case 'file':
                cls += ' semantic-none';
                iconCls = 'fas fa-file';
                break;
              case 'uploaded':
                cls += ' semantic-incall';
                iconCls = 'fas fa-circle-arrow-up';
                break;
              case 'downloading':
                cls += ' semantic-incall';
                iconCls = 'fas fa-circle-arrow-down';
                break;
              case 'pending':
                cls += ' semantic-incall';
                iconCls = 'fas fa-arrow-progress';
                break;
              case 'success':
                cls += ' semantic-good';
                iconCls = 'fas fa-circle-check';
                break;
              case 'empty':
                cls += ' semantic-unknown';
                iconCls = 'far fa-file';
                break;
              case 'ignored':
                cls += ' semantic-unknown';
                iconCls = 'far fa-circle';
                break;
              case 'warn':
                cls += ' semantic-fair';
                iconCls = 'fas fa-square-bolt';
                break;
              case 'error':
                cls += ' semantic-bad';
                iconCls = 'fas fa-circle-xmark';
                break;
              case 'xerror':
                cls += ' semantic-critical';
                iconCls = 'fas fa-triangle-exclamation';
                break;
              default:
                cls += ' semantic-unknown';
            }
          }

          if (iconCls) filter.fa = iconCls;
          filter.class = cls;
        }

        return (this.currentFilter.field !== 'event_level' && this.currentFilter.field !== 'fctnetworkinsightsxml.severity' && this.currentFilter.field !== 'system_name' && this.currentFilter.field !== 'name' && this.currentFilter.field !== 'status') ? // this really needs to be more generic
            values.map(value => {
              value.imgUrl = getLogoImage(value.name);
              return value;
            }) : (this.currentFilter.field === 'event_level' || this.currentFilter.field === 'fctnetworkinsightsxml.severity') ? values.map(value => {
              updateFilterForEventLevel(value);
              return value;
            }) : (this.currentFilter.field === 'status') ? values.map(value => {
                  updateFilterForAdrStatuses(value);
                  return value;
                }) :
                [...values];
      },
    },
  }
  function populateFilterValueAndText(filter) {
    if (filter.type === 'string') {
      filter.value = filter.field + filter.operatorValue.replace('|', escapeSpecialChars(filter.queryVar));
    } else if (filter.type === 'uuid') {
      filter.value = filter.field + filter.operatorValue.replace('|', escapeSpecialChars(filter.queryVar));
    } else if (filter.type === 'number') {
      filter.value = filter.field + filter.operatorValue + UtilsService.convertToSeconds(filter.queryVar);
    } else if (filter.type === 'list') {
      let tags = filter.queryVar.trim().split(',').map((tag) => tag.trim());
      filter.value = '';
      tags.forEach(function(tag) {
        filter.value += filter.field + filter.operatorValue.replace('|', escapeSpecialChars(tag));
      });
    } else if (filter.type === 'starRating') {
      let tags = filter.queryVar.trim().split(',').map((tag) => tag.trim());
      filter.value = '';
      tags.forEach(function(tag) {
        filter.value += filter.field + filter.operatorValue.replace('|', escapeSpecialChars(tag));
      });
    } else if (filter.type === 'metric') {
      filter.value = filter.operatorValue.replace('|', filter.field).replace('|', escapeSpecialChars(filter.queryVar));
    } else if (filter.type === 'metricList') {
      filter.value = filter.operatorValue.replace('|', filter.field).replace('|', escapeSpecialChars(filter.queryVar));
    } else if (filter.type === 'metricStarRating') {
      let tags = filter.queryVar.trim().split(',').map((tag) => tag.trim());
      filter.value = '';
      tags.forEach(function(tag) {
        filter.value += filter.operatorValue.replace('|', filter.field).replace('|', escapeSpecialChars(tag));
      });
    }
    filter.text = filter.fieldText + ' ' + filter.operatorText + ' ' + filter.queryVar;
    filter.shortText = filter.queryVar;
  }
  function escapeSpecialChars(userVal) {
    let escaped = userVal;
    escaped = escaped.split('\\').join('\\\\\\\\'); // do backslash first so other escapes don't interfere
    escaped = escaped.split('*').join('\\\\*');
    // escaped = escaped.split("'").join("\\'"); // maybe no need to escape '?
    escaped = escaped.split('"').join('\\"');
    return escaped;
  }
  function compareName( a, b ) {
    if ( a.name < b.name ){
      return -1;
    }
    if ( a.name > b.name ){
      return 1;
    }
    return 0;
  }
  function compareText( a, b ) {
    if ( a.text < b.text ){
      return -1;
    }
    if ( a.text > b.text ){
      return 1;
    }
    return 0;
  }
  function compareTitle( a, b ) {
    if ( a.title < b.title ){
      return -1;
    }
    if ( a.title > b.title ){
      return 1;
    }
    return 0;
  }

  // from galaxy-admin-web
  function getLogoImage(key) {
    let imgUrl = '';
    switch(key) {
      case "ACMEPACKET": imgUrl = '20x20-oracle-acme-pack.png'; break;
      case "AURASM": imgUrl = '20x20-avaya.png'; break;
      case "ACANO": case "CMS": imgUrl = '20x20-cms.png'; break;
      case "BJN": case "BlueJeans": imgUrl = '20x20-bluejeans.png'; break;
      case "CISCOCH": case "CTPS": case "CUBE": case "CUCM": case "CUCM CDR": case "MCU": 
      case "TMS": case "TPE": case "VCS": case "VCSX": imgUrl = '20x20-cisco.png'; break;
      case "CHIME": imgUrl = '20x20-amazonChime.png'; break;
      case "CUSTOM": case "CSV": imgUrl = '20x20-csv.png'; break;
      case "DMA": case "RPRM": case "RMX": case "RPAD": imgUrl = '20x20-poly.png';;  break;
      case "EPM": imgUrl = '20x20-vyopta.png'; break;
      case "EXCHG": imgUrl = '20x20-ms-exch.png'; break;
      case "GCAL": imgUrl = '20x20-gcal.png'; break;
      case "GMEET": case "GMEET CDR": case "Google Meet": imgUrl = '20x20-gmeet.png'; break;
      case "GOTO": imgUrl = '20x20-goto.png'; break;
      case "ISDN": imgUrl = 'isdn.jpg'; break;
      case "MSTEAMS": case "MSTEAMS CDR": case "MSTEAMS DR CDR": case "MSTEAMS PSTN CDR": imgUrl = '20x20-ms-teams.png'; break;
      case "OFFICE365": imgUrl = '20x20-off365.png'; break;
      case "PEXIP": case "PEXIPERM": imgUrl = '20x20-pexip.png'; break;
      case "RINGCENTRAL": imgUrl = '20x20-ringcentral.png'; break;
      case "SKYPE": case "LYNC": imgUrl = 'skype.png'; break;
      case "VIDYO": imgUrl = 'vidyo.png'; break;
      case "WEBEX": case "WEBEX (legacy)": case "CISCOWEBEX": case "CSPRK": imgUrl = 'webex.png'; break;
      case "ZOOM": case "ZOOMPHONE": imgUrl = 'zoom.png'; break;
      default: break;
    }
    if (imgUrl) {
      imgUrl = 'dist/images/systems/' + imgUrl;
    }
    return imgUrl;
  }

  function constructDatasetQuickPickFilter(filter, valueDescriptions, excludedValues, excludedDescriptions, onlyClicked, otherDeselected) {
    let values = filter.data.selections;
    if (onlyClicked && values.length === 1 && values[0].toLowerCase() !== 'other') {
      // when the user clicks "only" we want to set up an "equals" filter
      filter.value = `${filter.field}=="${values[0]}"`;
      filter.queryVar = values[0];
      filter.text = `${filter.fieldText} is ${valueDescriptions[0]}`;
      filter.shortText = valueDescriptions[0];
    } else {
      if (excludedValues.length && !otherDeselected) {
        // default filter behavior, apply "not in" filter
        filter.value = `${filter.field}=out=(${excludedValues.join()})`;
        filter.queryVar = excludedValues.join();
        filter.text = `${filter.fieldText} is not (${excludedDescriptions.join()})`;
        filter.shortText = excludedDescriptions.join();
      } else if (otherDeselected && values.length) {
        // if the QP has an "other" option and it is deselected, we have to create an "in" filter instead
        filter.value = `${filter.field}=in=(${values.join()})`;
        filter.queryVar = values.join();
        filter.text = `${filter.fieldText} is (${valueDescriptions.join()})`;
        filter.shortText = valueDescriptions.join();
      } else if (otherDeselected && !values.length) {
        // TODO: is there a better way to handle "Other" case when all options are deselected?
        // we have to create a filter that will return no results so that the user sees an empty dataset
        filter.value = `${filter.field}=in=(ReturnNoResults)`;
        filter.queryVar = 'ReturnNoResults';
        // This isn't technically what our filter is, but it should be a good representation to the user
        filter.text = `${filter.fieldText} is not (${excludedDescriptions.join()})`;
        filter.shortText = excludedDescriptions.join();
      } else {
        // this should never get hit, but just in case something goes wrong, apply no filter
        filter.value = '';
        filter.queryVar = '';
        filter.text = '';
        filter.shortText = '';
      }
    }
  }

  function constructMetricQuickPickFilter(filter, valueDescriptions, excludedValues, excludedDescriptions, onlyClicked, otherDeselected) {
    let values = filter.data.selections;
    if (onlyClicked && values.length === 1 && values[0].toLowerCase() !== 'other') {
      // when the user clicks "only" we want to set up an "equals" filter
      filter.value =  `tags=="${filter.field}=${values[0].toLowerCase()}"`;
      filter.queryVar = values[0].toLowerCase();
      filter.text = `${filter.field} is ${valueDescriptions[0]}`;
      filter.shortText = valueDescriptions[0];
    } else {
      if (excludedValues.length && !otherDeselected) {
        // default filter behavior, apply "not in" filter
        filter.value = 'tags=out=(' + excludedValues.map(val => `"${filter.field}=${val.toLowerCase()}"`).join() + ')';
        filter.queryVar = excludedValues.map(val => val.toLowerCase()).join();
        filter.text = `${filter.field} is not (${excludedDescriptions.join(', ')})`;
        filter.shortText = excludedDescriptions.join(', ');
      } else if (otherDeselected && values.length) {
        // if the QP has an "other" option and it is deselected, we have to create an "in" filter instead
        // TODO: Untested. Currently no metric quick picks with "other" option.
        filter.value = 'tags=in=(' + values.map(val => `"${filter.field}=${val.toLowerCase()}"`).join() + ')';
        filter.queryVar = values.join();
        filter.text = `${filter.fieldText} is (${valueDescriptions.join(', ')})`;
        filter.shortText = valueDescriptions.join(', ');
      } else if (otherDeselected && !values.length) {
        // TODO: is there a better way to handle "Other" case when all options are deselected?
        // we have to create a filter that will return no results so that the user sees an empty dataset
        // TODO: Untested. Currently no metric quick picks with "other" option.
        filter.value = `tags=in=("${filter.field}=ReturnNoResults")`;
        filter.queryVar = 'ReturnNoResults';
        // This isn't technically what our filter is, but it should be a good representation to the user
        filter.text = `${filter.fieldText} is not (${excludedDescriptions.join(', ')})`;
        filter.shortText = excludedDescriptions.join(', ');
      } else {
        // this should never get hit, but just in case something goes wrong, apply no filter
        filter.value = '';
        filter.queryVar = '';
        filter.text = '';
        filter.shortText = '';
      }
    }
  }

  function contructPeripheralQuickPickFilter(filter) {
    let qpValues = filter.data.selections;
    // microphone is a special case and the filters can be slightly different
    let isMicrophone = filter.field.toLowerCase() === 'microphone';

    // qpValues will be in the set ("Good", "Bad", "Neutral")
    // TODO: should we set filter.queryVar for these peripherals filters? if so, what should it be?
    if (_.isEqual(qpValues, ['Good'])) {
      // e.g. [(Microphone does not contain Connected=none | Not Connected=none) AND (Microphone contains Not Connected=none)]
      filter.value = isMicrophone ? filter.field + '!="*Connected=none | Not Connected=none*";' + filter.field + '=="*Not Connected=none*"' :
                                    filter.field + '!="Connected=none | Not Connected=none";' + filter.field + '=="*Not Connected=none*"';
      filter.text = `${filter.fieldText} status is Available`;
      filter.shortText = 'Available';
    } else if (_.isEqual(qpValues, ['Bad'])) {
      // e.g. [(Microphone does not equal Unknown) AND (Microphone does not contain Not Connected=none)]
      filter.value = filter.field + '!="Unknown";' + filter.field + '!="*Not Connected=none*"';
      filter.text = `${filter.fieldText} status is Not Available`;
      filter.shortText = 'Not Available';
    } else if (_.isEqual(qpValues, ['Neutral'])) {
      // e.g. [(Microphone contains Connected=none | Not Connected=none) OR (Microphone equals Unknown)]
      filter.value = isMicrophone ? filter.field + '=="*Connected=none | Not Connected=none*",' + filter.field + '=="Unknown"' :
                                    filter.field + '=="Connected=none | Not Connected=none",' + filter.field + '=="Unknown"';
      filter.text = `${filter.fieldText} status is None`;
      filter.shortText = 'None';
    } else if (qpValues.includes('Good') && qpValues.includes('Bad') && !qpValues.includes('Neutral')) {
      // e.g. [(Microphone does not contain Connected=none | Not Connected=none) AND (Microphone does not equal Unknown)]
      filter.value = isMicrophone ? filter.field + '!="*Connected=none | Not Connected=none*";' + filter.field + '!="Unknown"' :
                                    filter.field + '!="Connected=none | Not Connected=none";' + filter.field + '!="Unknown"';
      filter.text = `${filter.fieldText} status is (Available, Not Available)`;
      filter.shortText = 'Available, Not Available';
    } else if (qpValues.includes('Good') && qpValues.includes('Neutral') && !qpValues.includes('Bad')) {
      // e.g. [(Microphone contains Not Connected=none) OR (Microphone equals Unknown)]
      filter.value = filter.field + '=="*Not Connected=none*",' + filter.field + '=="Unknown"';
      filter.text = `${filter.fieldText} status is (Available, None)`;
      filter.shortText = 'Available, None';
    } else if (qpValues.includes('Bad') && qpValues.includes('Neutral') && !qpValues.includes('Good')) {
      // e.g. [(Microphone contains Connected=none | Not Connected=none) OR (Microphone equals Unknown) OR (Microphone does not contain Not Connected=none)]
      filter.value = isMicrophone ? filter.field + '=="*Connected=none | Not Connected=none*",' + filter.field + '=="Unknown",' + filter.field + '!="*Not Connected=none*"' :
                                    filter.field + '=="Connected=none | Not Connected=none",' + filter.field + '=="Unknown",' + filter.field + '!="*Not Connected=none*"';
      filter.text = `${filter.fieldText} status is (Not Available, None)`;
      filter.shortText = 'Not Available, None';
    } else if (!qpValues.length) {
      // e.g. [(Microphone does not equal Unknown) AND (Microphone does not contain Connected)]
      filter.value = filter.field + '!="Unknown";' + filter.field + '!="*Connected*"';
      filter.text = `${filter.fieldText} status is not (Available, Not Available, None)`;
      filter.shortText = 'Available, Not Available, None';
    } else {
      filter.value = '';
    }
  }
</script>

<style lang="scss" scoped>
.vyopta-single-filter {

  .new-beta.badge {
    width: 41px;
    height: 16px;
    background-color: #8085EA;
    line-height: 9px;
    border: #ffffff4d solid thin;
    color: #FFFFFF;
    font-style: italic;
    border-radius: 4px;
    text-align: center;
    font-weight: 700;
    font-size: 11px;
  }

  &.quick-pick {
    .header, .field, .operator, .value, .textValue, .optionsBox {
      width: 100%;
    }
  }
  
  > .newFilter:hover {
    > .header > .rightHeader > .delete {
      display: inline-block;
    }
  }

  position: relative;
  .pillOnly {
    cursor: pointer;
    font-size: 12px;
    line-height: 25px;
    padding: 8px 12px;
    background-color: var(--filter-bg-color);
    border-radius: var(--border-radius);
    height: 41px;
    width: 198px;
    .pillText {
      width: calc(100% - 18px); // leave room for the x button
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      display: inline-block;
      color: var(--text-color)
    }
  }
  .header {
    line-height: 20px; // override of hack to eliminate empty space under "in" combos
    position: relative;
    bottom: 3px;
    height: 17px;
    .leftHeader {
      display: inline-flex;
      max-width: 100%;
      padding-right: 15px;

      &.static {
        cursor: default;
        pointer-events: none;
      }

      .fieldName, .operatorName {
        display: inline-block;
        font-size: 14px;
        font-weight: 600;
        letter-spacing: unset;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .fieldName {
        color: var(--filter-text-color);
        text-transform: capitalize;
      }
      .operatorName {
        cursor: pointer;
        color: var(--hover-color);
        margin-left: 8px;
        &:hover {
          text-decoration: underline;
        }
        &.qualityFilterHeader {
        }
      }
    }
    .rightHeader {
      display: inline-block;
      position: absolute;
      right: -7px;
      top: 2px;
      z-index: 100;
      .delete {
        cursor: pointer;
        display: none;
        font-size: 15px;
        color: var(--primary-btn-bg-color);
        --fa-primary-color: var(--primary-btn-color);
        --fa-secondary-color: var(--primary-btn-hover-bg-color);
        --fa-secondary-opacity: 1;
        --fa-primary-opacity: 1;
        &:hover {
          color:  var(--primary-btn-hover-bg-color);
          --fa-secondary-color:  var(--primary-btn-hover-bg-color);
        }
      }
    }
  }
  .filterValueSelect.shown{
    line-height: 1.5;
  }
  .field, .operator, .value, .filterValueSelect, .textValue, .optionsBox {
    display: none;
    width: 100%;
    max-width: 450px;
    &.shown {
      display: inline-block;
    }
  }
  .operator {
    .v-divider {
      margin-top: 8px;
      margin-bottom: 8px;
    }
  }
  .filterValueSelect {
    list-style: none;
    min-width: 198px;
    height: 40px;
    .filterLookalike {
      cursor: pointer;
      height: 40px;
      font-size: 14px;
      color: var(--filter-text-color);
      border-radius: var(--border-radius);
      width: 100%;
      display: inline-block;
      line-height: 18px;
      //padding: 8px 0;
      max-width: 100%;
      border: 1px solid var(--filter-border-color);
      padding: 10px 12px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      &.complete {
        background-color: var(--filter-bg-color);
        border: 1px solid var(--filter-border-color);
        &:hover {
          border: 1px solid var(--hover-color);
        }
      }
    }
    .optionsBox {
      position: absolute;
      background: white;
      z-index: 998;
      height: 40px;
      overflow-y: visible;
      border-radius: var(--border-radius);

      .selectOption {
        display: flex;
      }
      .optionName {
        flex-grow: 1;
      }

      .optionImage {
        height: 16px;
      }

    }
  }
}

.v-divider {
  margin-top: 8px;
  margin-bottom: 8px;
}

.quick-pick-text {
  flex: 1;
  white-space: nowrap;
}

.quick-pick-image {
  height: auto;
  width: auto;
  max-height: 24px;
  max-width: 92px;
  margin-left: 20px;
  flex: 1;
}

.info-btn {
  font-size: 9px;
  line-height: 9px;
  vertical-align: super;
  margin-left: -3px;
}

.quick-pick-tt-body {
  font-size: 10px;
  line-height: 10px;
  font-family: var(--font-family);
  font-weight: 500;
  padding-top: 6px;
  padding-bottom: 10px;
}

.quick-pick-tt-header {
  font-size: 10px;
  line-height: 10px;
  font-family: var(--font-family);
  font-weight: 800;
  padding-top: 0;

  i {
    font-size: 14px;
    margin-right: 2px;
  }

  .quick-pick-tt-header-text {
    position: relative;
    bottom: 2px;
  }

  .quick-pick-tt-header-middle-text {
    margin-left: 8px;
    margin-right: 8px;
  }
}

.quick-pick-tt {
  padding: 0;
  text-align-last: center;
  color: var(--tooltip-color);

  &.quick-pick-tt-legend {
    display: flex;

    .quick-pick-tt-legend-item {
      flex: 1;
      padding: 0 5px;

      &:first-child {
        padding-left: 0;
      }

      &:last-child {
        padding-right: 0;
      }

      .quick-pick-tt-legend-icon {
        font-size: 18px;
        line-height: 22px;
      }

      .quick-pick-tt-legend-svg .qp-option-svg {
        height: 18px;

        &.qp-option-green {
          height: 13px;
          margin-top: 5px;
          &.qp-option-camera {
            height: 11px;
            margin-top: 7px;
          }
        }
      }

      .quick-pick-tt-legend-label {
        margin-top: 1px;
        font-family: var(--font-family);
        font-weight: 500;
        font-size: 12px;
        white-space: nowrap;
        line-height: 12px;
      }
    }
  }
}
</style>

<style lang="scss">
// gigantic hacks to maximize the height of the dropdowns but keep them dynamic as the view gets shorter
  .field-input-menu, .vyopta-single-filter .vue-treeselect__menu {
    max-height: max(100px, 100vh - 300px) !important;
    border-bottom-left-radius: 24px;
    border-bottom-right-radius: 24px;
  }

  .assurance-panel-options .field-input-menu,
  .assurance-panel-options .vyopta-single-filter .vue-treeselect__menu,
  .panel-options .field-input-menu,
  .panel-options .vyopta-single-filter .vue-treeselect__menu {
    max-height: max(250px, 100vh - 410px) !important;
  }

  .alertDetails .field-input-menu {
    max-height: max(250px, 100vh - 680px) !important;
  }

  .alertDetails .eventSelection .vyopta-single-filter .vue-treeselect__menu {
    max-height: max(250px, 100vh - 390px) !important;
  }

  .alertDetails .eventSelection.lower-1 .vyopta-single-filter .vue-treeselect__menu {
    max-height: max(250px, 100vh - 530px) !important;
  }

  .v-subheader.filter-select-menu-header {  
    height: unset;
  }

  .vyopta-single-filter {
    // vuetify overrides
    .v-text-field.v-text-field--solo:not(.v-text-field--solo-flat)>.v-input__control>.v-input__slot {
      box-shadow: none;
      background-color: var(--panel-background);
      color: var(--text-color);
      input, .v-icon {
        color: var(--text-color);
      }
      input::placeholder {
        color: var(--soft-text-color);
      }
    }

    .v-input {
      height: 40px;
      min-height: 40px;
      border-radius: var(--border-radius);
      .v-input__slot {
        height: 40px;
        min-height: 40px;
        border: 1px solid var(--filter-border-color);
        margin: 0;
        input {
          font-size: 14px;
          border-radius: var(--border-radius);
          border-color:  var(--filter-border-color);
        }
        .v-label {
          font-size: 14px;
          line-height: 14px;
          font-weight: normal;
          color: #6d787e;
          padding: 0;
          margin: 0;
        }
        #placeholder {
          color: #6D787E;
        }
      }
      .v-text-field__details {
        display: none;
      }
    }
    .remove-filter {
      padding-left: 8px;
      padding-top: 5px;
      position: relative;
      bottom: 2px;
      font-size: 12px;
      visibility: hidden;
      float: right;
      margin-top: 4px;
    }

    &:hover .remove-filter {
      visibility: visible;
    }
    .value .complete .v-input__slot {
      background-color: var(--filter-bg-color);
      border: 1px solid var(--filter-border-color);
      input {
        color: #253035;
      }
      &:hover, &:focus-within {
        border: 1px solid var(--hover-color);
      }
    }

    .v-tooltip {
      display: inline-block;
    }
    .search .v-input__slot {
      border: none;
      border-radius: 24px 6px 0 0;
      border-bottom: 1px solid var(--filter-border-color);
    }
  }

  .single-filter-quick-pick-tt {
    &.v-tooltip__content {
      padding: 10px 15px;
      letter-spacing: unset;
      font-size: 12px;
      font-family: var(--font-family);
      border-radius: 3px;
      background: var(--filter-bg-color);

      &.above {
        padding: 5px 10px;
        pointer-events: auto;
        box-shadow: 0px 3px 6px #00000029;

        &:after {
          content: "";
          position: absolute;
          border-style: solid;
          border-width: 8px 8px 0 8px;
          border-top-color: inherit;
          border-bottom-color: inherit;
          border-left-color: transparent;
          border-right-color: transparent;
          display: block;
          width: 0;
          z-index: 1;
          bottom: 2px;
          left: 50%;
          transform: translate(-50%, 0px);
        }
      }
    }

    .quick-pick-tt-link a {
      font-size: 10px;
      line-height: 9px;
      font-family: var(--font-family);
      font-weight: 500;
      text-decoration: none;
      color: var(--hover-color);

      &:hover {
        text-decoration: underline;
      }
    }
  }

  .v-list.v-select-list {
    background-color: var(--panel-background);
    .v-list-item {
      .v-list-item__content {
        padding: 5px 0;
      }
      .v-list-item__title {
        font-size: 14px;
        color: var(--text-color);
        .v-list-item__mask {
          font-weight: bold;
          background: rgba(0, 0, 0, 0.1);
        }
      }
    }
  }
  .optionsMenu {
    //width: 198px; // todo
    .option-text {
      flex: 1;
    }

    .option-image {
      height: 16px;
      width: 20px;
      flex: 0;
    }

    hr {
      margin-top: 5px;
      margin-bottom: 5px;
    }
  }

  .v-treeview {
    .v-icon.v-icon {
      font-size: 18px;
    }
  }

  .vyopta-single-filter {
    .vue-treeselect {
      min-height: 40px;
      .vue-treeselect__control {
        min-height: 40px;
        border-top-left-radius: 20px; // height of filter is 40
        border-top-right-radius: 20px; // height of filter is 40
        background-color: var(--panel-background);
        color: var(--text-color);

        .vue-treeselect__placeholder, .vue-treeselect__single-value {
          line-height: 40px;
        }
        .vue-treeselect__multi-value-item,
        .vue-treeselect__value-remove {
          background-color: var(--background-color);
          color: var(--default-color);
        }
        .vue-treeselect__multi-value-item:hover .vue-treeselect__value-remove {
          color: var(--hover-color);
        }
        .vue-treeselect__x-container:hover {
          color: var(--hover-color);
        }      
        .vue-treeselect__input-container {
          width: 100%; // fix bug there you can't click in the whitespace after text to move cursor to end
        }
        .vue-treeselect__input-container input { 
          width: 100% !important; // fix bug there you can't click in the whitespace after text to move cursor to end
        }
      }

      .vue-treeselect__menu {
        background-color: var(--panel-background);
        scrollbar-color: var(--scrollbar-thumb-color) var(--panel-background);
        color: var(--text-color);
        .vue-treeselect__option--highlight {
          background-color: var(--active-background);
        }
      }
      .vue-treeselect__menu-container {
        .vue-treeselect__checkbox--checked,
        .vue-treeselect__checkbox--checked:hover,
        .vue-treeselect__label-container:hover .vue-treeselect__checkbox--checked {
          border-color: var(--default-color);
          background: var(--default-color);
        }
        .vue-treeselect__label-container:hover .vue-treeselect__checkbox--unchecked {
          border-color: var(--default-color);
        }

      }


      &.vue-treeselect--has-value .vue-treeselect__multi-value {
        margin-bottom: 0; 
      }
      
      &.vue-treeselect--multi .vue-treeselect__input-container {
        font-size: inherit;
      }
      &.vue-treeselect--searchable:not(.vue-treeselect--disabled) .vue-treeselect__value-container {
        cursor: unset; // fix bug where you have a text cursor but can't click to move cursor because it on div margin
      }
      &.vue-treeselect--searchable:not(.vue-treeselect--disabled) .vue-treeselect__value-container input {
        cursor: text; // fix bug where you have a text cursor but can't click to move cursor because it on div margin
      }
    }
    
    
    label.vue-treeselect__label {
      white-space: normal;
      font-weight: normal;
      line-height: normal;
      padding-top: 9px;
      padding-bottom: 9px;
      margin-bottom: 0;
    }

    label.vue-treeselect__label.tree-branch {
      font-weight: bold;
    }
    .vue-treeselect__option {
      padding-right: 10px;
    }
    .vue-treeselect__indent-level-0 .vue-treeselect__option {
      padding-left: 10px;
    }
    .vue-treeselect__indent-level-1 .vue-treeselect__option {
      padding-left: 20px;
    }
    .vue-treeselect__indent-level-2 .vue-treeselect__option {
      padding-left: 35px;
    }
    .vue-treeselect__indent-level-3 .vue-treeselect__option {
      padding-left: 50px;
    }
    .vue-treeselect__indent-level-4 .vue-treeselect__option {
      padding-left: 65px;
    }
    .vue-treeselect__indent-level-5 .vue-treeselect__option {
      padding-left: 80px;
    }
    .vue-treeselect__indent-level-6 .vue-treeselect__option {
      padding-left: 95px;
    }
    .vue-treeselect__indent-level-7 .vue-treeselect__option {
      padding-left: 110px;
    }
    .vue-treeselect__indent-level-8 .vue-treeselect__option {
      padding-left: 125px;
    }
    .vue-treeselect__option-arrow-placeholder {
      width: 0;
    }
    .vue-treeselect__multi-value-item-container {
      //display: none;
    }
    .vue-treeselect__x-container svg {
      position: absolute;
      top: 10px;
    }
  }

</style>
